import { inject, Injectable } from '@angular/core';
import { DemoApiClient } from '@system-dyg/core/demo-api';
import { map, Observable, shareReplay } from 'rxjs';
import {
  CfdiDashboardResponse,
  CfdiDetail,
  CfdiDownloadHistoryResponse,
  CfdiListQuery,
  CfdiListItem,
  CfdiListResponse,
  CfdiPeriodQuery,
} from './cfdi.models';

@Injectable({ providedIn: 'root' })
export class CfdiDemoApiService {
  private readonly api = inject(DemoApiClient);
  private readonly listCache = new Map<string, Observable<readonly CfdiListItem[]>>();

  get enabled(): boolean {
    return this.api.enabled;
  }

  list(query: CfdiListQuery = {}): Observable<CfdiListResponse> {
    return this.api.get<CfdiListResponse>('/api/cfdi', { ...query });
  }

  listAll(
    direction: 'issued' | 'received',
    type: 'I' | 'E' | 'T' | 'N' | 'P',
  ): Observable<readonly CfdiListItem[]> {
    const key = `${direction}:${type}`;
    const cached = this.listCache.get(key);
    if (cached) return cached;

    const request = this.list({
      direction,
      type,
      pageSize: 1000,
    }).pipe(
      map(response => response.data),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.listCache.set(key, request);
    return request;
  }

  detail(uuid: string): Observable<CfdiDetail> {
    return this.api.get<CfdiDetail>(
      `/api/cfdi/${encodeURIComponent(uuid)}`,
    );
  }

  dashboard(query: CfdiPeriodQuery): Observable<CfdiDashboardResponse> {
    return this.api.get<CfdiDashboardResponse>('/api/cfdi/dashboard', {
      ...query,
    });
  }

  downloadHistory(
    query: Omit<CfdiPeriodQuery, 'month'>,
  ): Observable<CfdiDownloadHistoryResponse> {
    return this.api.get<CfdiDownloadHistoryResponse>(
      '/api/cfdi/download-history',
      { ...query },
    );
  }
}
